/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: BulkModificationPage.tsx
 🗓️ Created: 2025-10-16
 🧩 Description:
    Bulk Modification page - allows batch updates to multiple extensions.
    Single-page interface with modal for extension selection.

 🔧 Notes:
    - Modal for selecting extensions with search
    - Configure modifications on main page
    - Apply changes to all selected extensions at once

 🔁 Last Updated: 2025-10-16 by Rodrigo Cuadra
────────────────────────────────────────────────────────────────*/

import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, Settings, Search, X, Plus, Eye, Filter, Hash, Type } from 'lucide-react';
import { useI18n } from '../../hooks/useI18n';
import { useDebugMode } from '../../hooks/useDebugMode';
import { useNotifications } from '../../contexts/NotificationContext';
import { useContextStore } from '../../store/context';
import { buildApiUrl, getApiHeaders } from '../../config/api';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import Toggle from '../../components/ui/Toggle';
import SelectClassOfService from '../../components/ui/SelectClassOfService';
import SimpleTooltip from '../../components/ui/SimpleTooltip';
import { LanguageSelector } from '../extensions/components/LanguageSelector';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { ModuleFormHeader } from '../../components/layouts';
import { ExtensionSelectorModal } from './components/ExtensionSelectorModal';
import FixedActionBar from '../../components/ui/FixedActionBar';

type SelectionTab = 'individual' | 'ranges' | 'patterns' | 'filters';

interface Extension {
  id: number | string;
  uuid: string;
  extension: string;
  name: string | null;
  enabled: boolean;
  class_of_service_name?: string;
}

interface ClassOfService {
  id: number;
  name: string;
}

/**
 * Payload for bulk-modification preview and apply requests.
 * Selection can be by IDs, number ranges, patterns, or filter criteria.
 */
interface BulkPayload {
  domainId?: number;
  extensionIds?: (number | string)[];
  ranges?: string[];
  patterns?: string[];
  filters?: {
    classOfServicesId?: number;
    enabled?: boolean;
    voicemailEnabled?: boolean;
    billingEnabled?: boolean;
    type?: string;
    languageCode?: string;
  };
  modifications?: {
    classOfServicesId?: number | null;
    callTimeout?: number;
    languageCode?: string;
    accountCode?: string | null;
    recording?: {
      recordIncoming: boolean;
      recordOutgoing: boolean;
      recordInternal: boolean;
      recordOnDemand: boolean;
      recordingFormat: string;
      recordPath: string;
      recordTemplate: string;
    };
  };
}

export default function BulkModificationPage() {
  const navigate = useNavigate();
  const { t } = useI18n();
  const { isDebugModeActive } = useDebugMode();
  const { showSuccess, showError } = useNotifications();
  const { selectedDomain } = useContextStore();

  // Modal and selection state
  const [showExtensionSelector, setShowExtensionSelector] = useState(false);
  const [selectedExtensions, setSelectedExtensions] = useState<Extension[]>([]);
  const [isApplying, setIsApplying] = useState(false);
  const [isPreviewing, setIsPreviewing] = useState(false);
  const [previewCount, setPreviewCount] = useState<number>(0);

  // Selection tabs (individual, ranges, patterns, filters)
  const [selectionTab, setSelectionTab] = useState<SelectionTab>('individual');
  const buildTooltipContent = useCallback((key: string, debugKey?: string) => {
    if (!isDebugModeActive || !debugKey) {
      return t(key);
    }
    return `${t(key)}\n\n${t('extensions.debug.field')}: ${t(debugKey)}`;
  }, [isDebugModeActive, t]);


  // Advanced selection states
  const [ranges, setRanges] = useState<string[]>([]);
  const [rangeStart, setRangeStart] = useState('');
  const [rangeEnd, setRangeEnd] = useState('');

  const [patterns, setPatterns] = useState<string[]>([]);
  const [patternInput, setPatternInput] = useState('');

  const [filters, setFilters] = useState({
    classOfServicesId: null as number | null,
    enabled: null as boolean | null,
    voicemailEnabled: null as boolean | null,
    billingEnabled: null as boolean | null,
    type: '',
    languageCode: ''
  });

  // Search state for modal
  const [extensionSearch, setExtensionSearch] = useState('');
  const [searchResults, setSearchResults] = useState<Extension[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isLoadingAll, setIsLoadingAll] = useState(false);

  // Options
  const [classOfServices, setClassOfServices] = useState<ClassOfService[]>([]);
  const [isLoadingOptions, setIsLoadingOptions] = useState(false);

  // Modification form data
  const [modifications, setModifications] = useState({
    applyClassOfService: false,
    classOfServicesId: null as number | null,
    applyRingTime: false,
    ringTime: 30,
    applyLanguage: false,
    languageCode: 'en',
    applyAccountCode: false,
    accountCode: '',
    applyRecording: false,
    recordIncoming: false,
    recordOutgoing: false,
    recordInternal: false,
    recordOnDemand: false,
    recordingFormat: 'wav',
    recordPath: '/recordings',
    recordTemplate: '${caller_id_number}-${uuid}.wav',
  });

  // Load options on mount
  useEffect(() => {
    const loadOptions = async () => {
      try {
        setIsLoadingOptions(true);

        // Load Class of Services
        const cosResponse = await fetch(
          buildApiUrl('/api/telephony/class-of-services?limit=1000'),
          { headers: getApiHeaders() }
        );
        if (cosResponse.ok) {
          const cosResult = await cosResponse.json();
          setClassOfServices(cosResult.data || []);
        }
      } catch (error) {
        console.error('Error loading options:', error);
      } finally {
        setIsLoadingOptions(false);
      }
    };

    loadOptions();
  }, []);

  // Search extensions for modal
  const searchExtensions = useCallback(async (search: string) => {
    setIsSearching(true);
    try {
      const params = new URLSearchParams({
        page: '1',
        limit: '50',
        sortBy: 'extension',
        sortOrder: 'asc',
      });

      // Add search parameter only if search string is provided
      if (search && search.trim().length > 0) {
        params.append('search', search.trim());
      }

      // Add domain filter
      if (selectedDomain?.id) {
        params.append('domainId', selectedDomain.id.toString());
      }

      const response = await fetch(
        buildApiUrl(`/api/telephony/extensions?${params}`),
        { headers: getApiHeaders() }
      );

      if (response.ok) {
        const result = await response.json();
        if (result.success && result.data) {
          setSearchResults(result.data);
        }
      }
    } catch (error) {
      console.error('Error searching extensions:', error);
    } finally {
      setIsSearching(false);
    }
  }, [selectedDomain?.id]);

  // Load extensions when modal opens or search changes
  useEffect(() => {
    if (!showExtensionSelector) return;

    const timer = setTimeout(() => {
      searchExtensions(extensionSearch);
    }, extensionSearch ? 800 : 0); // No delay for initial load

    return () => clearTimeout(timer);
  }, [extensionSearch, showExtensionSelector, searchExtensions]);

  // Toggle extension selection in modal
  const toggleExtensionInModal = (ext: Extension) => {
    const isSelected = selectedExtensions.some(e => (e.uuid || e.id) === (ext.uuid || ext.id));
    if (isSelected) {
      setSelectedExtensions(prev => prev.filter(e => (e.uuid || e.id) !== (ext.uuid || ext.id)));
    } else {
      setSelectedExtensions(prev => [...prev, ext]);
    }
  };

  // Remove extension from selected list
  const removeExtension = (extensionId: number | string) => {
    setSelectedExtensions(prev => prev.filter(e => (e.uuid || e.id) !== extensionId));
  };

  // Select all extensions
  const selectAllExtensions = async () => {
    setIsLoadingAll(true);
    try {
      const params = new URLSearchParams({
        page: '1',
        limit: '10000', // Get all extensions
        sortBy: 'extension',
        sortOrder: 'asc',
      });

      // Add domain filter
      if (selectedDomain?.id) {
        params.append('domainId', selectedDomain.id.toString());
      }

      const response = await fetch(
        buildApiUrl(`/api/telephony/extensions?${params}`),
        { headers: getApiHeaders() }
      );

      if (response.ok) {
        const result = await response.json();
        if (result.success && result.data) {
          // Add all extensions that are not already selected
          const newExtensions = result.data.filter(
            (ext: Extension) => !selectedExtensions.some(e => (e.uuid || e.id) === (ext.uuid || ext.id))
          );
          setSelectedExtensions(prev => [...prev, ...newExtensions]);
          showSuccess(t('bulkModification.allExtensionsSelected', { count: result.data.length }));
        }
      }
    } catch (error) {
      console.error('Error loading all extensions:', error);
      showError(t('bulkModification.errorLoadingExtensions'));
    } finally {
      setIsLoadingAll(false);
    }
  };

  // Add range
  const handleAddRange = () => {
    if (rangeStart && rangeEnd) {
      setRanges(prev => [...prev, `${rangeStart}-${rangeEnd}`]);
      setRangeStart('');
      setRangeEnd('');
    }
  };

  // Remove range
  const handleRemoveRange = (index: number) => {
    setRanges(prev => prev.filter((_, i) => i !== index));
  };

  // Add pattern
  const handleAddPattern = () => {
    if (patternInput) {
      setPatterns(prev => [...prev, patternInput]);
      setPatternInput('');
    }
  };

  // Remove pattern
  const handleRemovePattern = (index: number) => {
    setPatterns(prev => prev.filter((_, i) => i !== index));
  };

  // Preview selection
  const handlePreview = async () => {
    // Validate that something is selected
    if (selectionTab === 'individual' && selectedExtensions.length === 0) {
      showError(t('bulkModification.messages.noExtensionsSelected'));
      return;
    }
    if (selectionTab === 'ranges' && ranges.length === 0) {
      showError(t('bulkModification.messages.noRanges'));
      return;
    }
    if (selectionTab === 'patterns' && patterns.length === 0) {
      showError(t('bulkModification.messages.noPatterns'));
      return;
    }
    if (selectionTab === 'filters') {
      const hasFilters = filters.classOfServicesId ||
        filters.enabled !== null ||
        filters.voicemailEnabled !== null ||
        filters.billingEnabled !== null ||
        filters.type ||
        filters.languageCode;
      if (!hasFilters) {
        showError(t('bulkModification.messages.noFilters'));
        return;
      }
    }

    setIsPreviewing(true);
    try {
      const payload: BulkPayload = {};

      // Add domain filter
      if (selectedDomain?.id) {
        payload.domainId = selectedDomain.id;
      }

      if (selectionTab === 'individual' && selectedExtensions.length > 0) {
        payload.extensionIds = selectedExtensions.map(e => e.id);
      } else if (selectionTab === 'ranges' && ranges.length > 0) {
        payload.ranges = ranges;
      } else if (selectionTab === 'patterns' && patterns.length > 0) {
        payload.patterns = patterns;
      } else if (selectionTab === 'filters') {
        payload.filters = {};
        if (filters.classOfServicesId) payload.filters.classOfServicesId = filters.classOfServicesId;
        if (filters.enabled !== null) payload.filters.enabled = filters.enabled;
        if (filters.voicemailEnabled !== null) payload.filters.voicemailEnabled = filters.voicemailEnabled;
        if (filters.billingEnabled !== null) payload.filters.billingEnabled = filters.billingEnabled;
        if (filters.type) payload.filters.type = filters.type;
        if (filters.languageCode) payload.filters.languageCode = filters.languageCode;
      }

      const response = await fetch(buildApiUrl('/api/telephony/bulk-modification/preview'), {
        method: 'POST',
        headers: getApiHeaders(),
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to preview');
      }

      const result = await response.json();
      setPreviewCount(result.count || 0);
      showSuccess(t('bulkModification.messages.previewSuccess', { count: result.count }));
    } catch (error) {
      const msg = error instanceof Error ? error.message : t('bulkModification.messages.previewError');
      showError(msg);
    } finally {
      setIsPreviewing(false);
    }
  };

  // Apply modifications
  const handleApply = async () => {
    // Build payload based on selection method
    const payload: BulkPayload = { modifications: {} };

    // Add domain filter
    if (selectedDomain?.id) {
      payload.domainId = selectedDomain.id;
    }

    // Selection method
    if (selectionTab === 'individual') {
      if (selectedExtensions.length === 0) {
        showError(t('bulkModification.messages.noExtensionsSelected'));
        return;
      }
      payload.extensionIds = selectedExtensions.map(e => e.id);
    } else if (selectionTab === 'ranges') {
      if (ranges.length === 0) {
        showError(t('bulkModification.messages.noRanges'));
        return;
      }
      payload.ranges = ranges;
    } else if (selectionTab === 'patterns') {
      if (patterns.length === 0) {
        showError(t('bulkModification.messages.noPatterns'));
        return;
      }
      payload.patterns = patterns;
    } else if (selectionTab === 'filters') {
      payload.filters = {};
      if (filters.classOfServicesId) payload.filters.classOfServicesId = filters.classOfServicesId;
      if (filters.enabled !== null) payload.filters.enabled = filters.enabled;
      if (filters.voicemailEnabled !== null) payload.filters.voicemailEnabled = filters.voicemailEnabled;
      if (filters.billingEnabled !== null) payload.filters.billingEnabled = filters.billingEnabled;
      if (filters.type) payload.filters.type = filters.type;
      if (filters.languageCode) payload.filters.languageCode = filters.languageCode;
    }

    // Build modifications
    if (modifications.applyClassOfService) {
      payload.modifications.classOfServicesId = modifications.classOfServicesId;
    }
    if (modifications.applyRingTime) {
      payload.modifications.callTimeout = modifications.ringTime;
    }
    if (modifications.applyLanguage) {
      payload.modifications.languageCode = modifications.languageCode;
    }
    if (modifications.applyAccountCode) {
      payload.modifications.accountCode = modifications.accountCode.trim() || null;
    }
    if (modifications.applyRecording) {
      payload.modifications.recording = {
        recordIncoming: modifications.recordIncoming,
        recordOutgoing: modifications.recordOutgoing,
        recordInternal: modifications.recordInternal,
        recordOnDemand: modifications.recordOnDemand,
        recordingFormat: modifications.recordingFormat,
        recordPath: modifications.recordPath,
        recordTemplate: modifications.recordTemplate,
      };
    }

    // Check if any modification is selected
    if (Object.keys(payload.modifications).length === 0) {
      showError(t('bulkModification.messages.noModificationsSelected'));
      return;
    }

    try {
      setIsApplying(true);

      const response = await fetch(buildApiUrl('/api/telephony/bulk-modification'), {
        method: 'POST',
        headers: getApiHeaders(),
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to apply modifications');
      }

      const result = await response.json();
      showSuccess(t('bulkModification.messages.success', { count: result.updatedCount }));

      // Reset all selection methods
      setSelectedExtensions([]);
      setRanges([]);
      setPatterns([]);
      setFilters({
        classOfServicesId: null,
        enabled: null,
        voicemailEnabled: null,
        billingEnabled: null,
        type: '',
        languageCode: ''
      });
      setPreviewCount(0);
      setModifications({
        applyClassOfService: false,
        classOfServicesId: null,
        applyRingTime: false,
        ringTime: 30,
        applyLanguage: false,
        languageCode: 'en',
        applyAccountCode: false,
        accountCode: '',
        applyRecording: false,
        recordIncoming: false,
        recordOutgoing: false,
        recordInternal: false,
        recordOnDemand: false,
        recordingFormat: 'wav',
        recordPath: '/recordings',
        recordTemplate: '${caller_id_number}-${uuid}.wav',
      });
    } catch (error) {
      const msg = error instanceof Error ? error.message : t('bulkModification.messages.error');
      showError(msg);
    } finally {
      setIsApplying(false);
    }
  };

  return (
    <div className="p-6">
      {/* Fixed Header */}
      <ModuleFormHeader
        icon={<Settings className="h-5 w-5 text-primary" />}
        title={t('bulkModification.title')}
        backButtonText={t('extensions.actions.list')}
        onBack={() => navigate('/pbx/extensions/extensions')}
        actions={[]}
      />

      {/* Main Content Card */}
      <div className="mt-8">
        <div className="bg-card border border-border rounded-lg p-6">
          <div className="space-y-6">
            {/* Selection Method - With Tabs */}
            <div className="space-y-3">
              <Label className="text-left font-semibold">
                {t('bulkModification.labels.selectExtensions')}
              </Label>

              <Tabs defaultValue="individual" value={selectionTab} onValueChange={(value) => setSelectionTab(value as SelectionTab)}>
                <TabsList>
                  <TabsTrigger value="individual" icon={<Users className="h-4 w-4" />}>
                    {t('bulkModification.tabs.individual')}
                  </TabsTrigger>
                  <TabsTrigger value="ranges" icon={<Hash className="h-4 w-4" />}>
                    {t('bulkModification.tabs.ranges')}
                  </TabsTrigger>
                  <TabsTrigger value="patterns" icon={<Type className="h-4 w-4" />}>
                    {t('bulkModification.tabs.patterns')}
                  </TabsTrigger>
                  <TabsTrigger value="filters" icon={<Filter className="h-4 w-4" />}>
                    {t('bulkModification.tabs.filters')}
                  </TabsTrigger>
                </TabsList>

                {/* Individual Selection */}
                <TabsContent value="individual">
                  <div className="space-y-3">
                    <div
                      onClick={() => setShowExtensionSelector(true)}
                      className="w-full px-4 py-3 border-2 border-dashed border-border rounded-lg hover:border-primary/50 hover:bg-primary/5 transition-all text-sm cursor-pointer"
                    >
                      {selectedExtensions.length === 0 ? (
                        <span className="text-muted-foreground">{t('bulkModification.clickToSelect')}</span>
                      ) : (
                        <div className="space-y-2">
                          {/* Extension chips - limited to ~2 lines (20 extensions) */}
                          <div className="flex items-start gap-2">
                            <div className="flex-1 flex items-center gap-1.5 flex-wrap max-h-[60px] overflow-hidden">
                              {selectedExtensions.slice(0, 20).map((ext) => (
                                <span
                                  key={ext.uuid || ext.id}
                                  className="inline-flex items-center gap-1 px-2 py-0.5 bg-primary/10 text-primary rounded text-xs font-mono"
                                >
                                  {ext.extension}
                                </span>
                              ))}
                              {selectedExtensions.length > 20 && (
                                <span className="inline-flex items-center px-2 py-0.5 bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300 rounded text-xs font-medium">
                                  +{selectedExtensions.length - 20} {t('bulkModification.more')}
                                </span>
                              )}
                            </div>
                            <div className="flex items-center gap-2 flex-shrink-0">
                              <span className="text-xs text-muted-foreground font-medium whitespace-nowrap">
                                {t('bulkModification.totalCount', { count: selectedExtensions.length })}
                              </span>
                              <button
                                onClick={(e) => { e.stopPropagation(); setSelectedExtensions([]); }}
                                className="px-2 py-1 text-xs text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors"
                              >
                                {t('common.clear')}
                              </button>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </TabsContent>

                {/* Ranges */}
                <TabsContent value="ranges">
                  <div className="space-y-3">
                    <div className="flex gap-2">
                      <Input
                        placeholder={t('bulkModification.placeholders.rangeStart')}
                        value={rangeStart}
                        onChange={(e) => setRangeStart(e.target.value)}
                        className="flex-1"
                      />
                      <span className="self-center text-muted-foreground">{t('common.to')}</span>
                      <Input
                        placeholder={t('bulkModification.placeholders.rangeEnd')}
                        value={rangeEnd}
                        onChange={(e) => setRangeEnd(e.target.value)}
                        className="flex-1"
                      />
                      <button
                        onClick={handleAddRange}
                        disabled={!rangeStart || !rangeEnd}
                        className="px-3 py-1.5 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                      >
                        <Plus className="h-4 w-4" />
                      </button>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {t('bulkModification.labels.rangeExample')}
                    </p>
                    {ranges.length > 0 && (
                      <div className="flex flex-wrap gap-2 pt-2">
                        {ranges.map((range, index) => (
                          <span
                            key={index}
                            className="inline-flex items-center gap-2 px-3 py-1.5 bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300 rounded-md text-sm font-medium"
                          >
                            <Hash className="h-3 w-3" />
                            {range}
                            <button
                              onClick={() => handleRemoveRange(index)}
                              className="hover:text-red-600 transition-colors"
                            >
                              <X className="h-3 w-3" />
                            </button>
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </TabsContent>

                {/* Patterns */}
                <TabsContent value="patterns">
                  <div className="space-y-3">
                    <div className="flex gap-2">
                      <Input
                        placeholder={t('bulkModification.placeholders.pattern')}
                        value={patternInput}
                        onChange={(e) => setPatternInput(e.target.value)}
                        className="flex-1"
                      />
                      <button
                        onClick={handleAddPattern}
                        disabled={!patternInput}
                        className="px-3 py-1.5 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                      >
                        <Plus className="h-4 w-4" />
                      </button>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {t('bulkModification.labels.patternExample')}
                    </p>
                    {patterns.length > 0 && (
                      <div className="flex flex-wrap gap-2 pt-2">
                        {patterns.map((pattern, index) => (
                          <span
                            key={index}
                            className="inline-flex items-center gap-2 px-3 py-1.5 bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300 rounded-md text-sm font-mono font-medium"
                          >
                            <Type className="h-3 w-3" />
                            {pattern}
                            <button
                              onClick={() => handleRemovePattern(index)}
                              className="hover:text-red-600 transition-colors"
                            >
                              <X className="h-3 w-3" />
                            </button>
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </TabsContent>

                {/* Filters */}
                <TabsContent value="filters">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs mb-1.5 block">{t('bulkModification.fields.classOfService.title')}</Label>
                      <SelectClassOfService
                        value={filters.classOfServicesId}
                        onChange={(value) => setFilters(prev => ({ ...prev, classOfServicesId: value !== null ? Number(value) : null }))}
                        classOfServices={classOfServices}
                      />
                    </div>
                    <div>
                      <Label className="text-xs mb-1.5 block">{t('bulkModification.labels.extensionStatus')}</Label>
                      <select
                        value={filters.enabled === null ? '' : filters.enabled.toString()}
                        onChange={(e) => setFilters(prev => ({ ...prev, enabled: e.target.value === '' ? null : e.target.value === 'true' }))}
                        className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                      >
                        <option value="">{t('bulkModification.filters.any')}</option>
                        <option value="true">{t('bulkModification.filters.enabledOnly')}</option>
                        <option value="false">{t('bulkModification.filters.disabledOnly')}</option>
                      </select>
                    </div>
                    <div>
                      <Label className="text-xs mb-1.5 block">{t('bulkModification.labels.voicemailStatus')}</Label>
                      <select
                        value={filters.voicemailEnabled === null ? '' : filters.voicemailEnabled.toString()}
                        onChange={(e) => setFilters(prev => ({ ...prev, voicemailEnabled: e.target.value === '' ? null : e.target.value === 'true' }))}
                        className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                      >
                        <option value="">{t('bulkModification.filters.any')}</option>
                        <option value="true">{t('common.enabled')}</option>
                        <option value="false">{t('common.disabled')}</option>
                      </select>
                    </div>
                    <div>
                      <Label className="text-xs mb-1.5 block">{t('bulkModification.labels.languageFilter')}</Label>
                      <LanguageSelector
                        value={filters.languageCode || ''}
                        onChange={(value) => {
                          // Save the complete value (e.g., "en-us-emma") including voice
                          setFilters(prev => ({ ...prev, languageCode: value || undefined }));
                        }}
                        showVoiceSelector={false}
                        showLabel={false}
                        className="text-sm"
                      />
                    </div>
                  </div>
                </TabsContent>
              </Tabs>

              {/* Preview Button - Aligned Right */}
              <div className="flex items-center justify-end gap-3">
                {previewCount > 0 && (
                  <span className="text-sm font-medium text-green-600 dark:text-green-400">
                    ✓ {t('bulkModification.messages.previewSuccess', { count: previewCount })}
                  </span>
                )}
                <button
                  onClick={handlePreview}
                  disabled={
                    isPreviewing ||
                    (selectionTab === 'individual' && selectedExtensions.length === 0) ||
                    (selectionTab === 'ranges' && ranges.length === 0) ||
                    (selectionTab === 'patterns' && patterns.length === 0) ||
                    (selectionTab === 'filters' && !filters.classOfServicesId && filters.enabled === null && filters.voicemailEnabled === null && filters.billingEnabled === null && !filters.type && !filters.languageCode)
                  }
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {isPreviewing ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                      {t('bulkModification.states.previewing')}
                    </>
                  ) : (
                    <>
                      <Eye className="h-4 w-4" />
                      {t('bulkModification.buttons.previewSelection')}
                    </>
                  )}
                </button>
              </div>
            </div>

            {/* Modifications Form */}
            <div className="space-y-4 pt-2">
              <h3 className="text-base font-semibold text-foreground">
                {t('bulkModification.configureModifications')}
              </h3>

              {/* Basic Settings - 2 Column Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {/* Class of Service */}
                <div className="border border-border rounded-lg p-3">
                  <div className="flex items-center gap-2 mb-2">
                    <input
                      type="checkbox"
                      id="applyClassOfService"
                      checked={modifications.applyClassOfService}
                      onChange={(e) => setModifications(prev => ({ ...prev, applyClassOfService: e.target.checked }))}
                      className="rounded border-border"
                    />
                    <Label htmlFor="applyClassOfService" className="text-left font-medium text-sm">
                      {t('bulkModification.fields.classOfService.title')}
                    </Label>
                  </div>
                  {modifications.applyClassOfService && (
                    <div className="ml-6">
                      <SelectClassOfService
                        value={modifications.classOfServicesId}
                        onChange={(value) => setModifications(prev => ({ ...prev, classOfServicesId: value !== null ? Number(value) : null }))}
                        classOfServices={classOfServices}
                        disabled={isLoadingOptions}
                      />
                    </div>
                  )}
                </div>

                {/* Language */}
                <div className="border border-border rounded-lg p-3">
                  <div className="flex items-center gap-2 mb-2">
                    <input
                      type="checkbox"
                      id="applyLanguage"
                      checked={modifications.applyLanguage}
                      onChange={(e) => setModifications(prev => ({ ...prev, applyLanguage: e.target.checked }))}
                      className="rounded border-border"
                    />
                    <Label htmlFor="applyLanguage" className="text-left font-medium text-sm">
                      {t('bulkModification.fields.language.title')}
                    </Label>
                  </div>
                  {modifications.applyLanguage && (
                    <div className="ml-6">
                      <LanguageSelector
                        value={modifications.languageCode}
                        onChange={(value) => {
                          // Save the complete value (e.g., "en-us-emma") including voice
                          setModifications(prev => ({ ...prev, languageCode: value }));
                        }}
                        showVoiceSelector={false}
                        showLabel={false}
                        className="text-sm"
                      />
                    </div>
                  )}
                </div>

                {/* Account Code */}
                <div className="border border-border rounded-lg p-3">
                  <div className="flex items-center gap-2 mb-2">
                    <input
                      type="checkbox"
                      id="applyAccountCode"
                      checked={modifications.applyAccountCode}
                      onChange={(e) => setModifications(prev => ({ ...prev, applyAccountCode: e.target.checked }))}
                      className="rounded border-border"
                    />
                    <Label htmlFor="applyAccountCode" className="text-left font-medium text-sm">
                      {t('bulkModification.fields.accountCode.title')}
                    </Label>
                  </div>
                  {modifications.applyAccountCode && (
                    <div className="ml-6">
                      <Input
                        value={modifications.accountCode}
                        onChange={(e) => setModifications(prev => ({ ...prev, accountCode: e.target.value }))}
                        placeholder={t('bulkModification.fields.accountCode.placeholder')}
                        className="text-sm"
                      />
                    </div>
                  )}
                </div>

                {/* Ring Time */}
                <div className="border border-border rounded-lg p-3">
                  <div className="flex items-center gap-2 mb-2">
                    <input
                      type="checkbox"
                      id="applyRingTime"
                      checked={modifications.applyRingTime}
                      onChange={(e) => setModifications(prev => ({ ...prev, applyRingTime: e.target.checked }))}
                      className="rounded border-border"
                    />
                    <Label htmlFor="applyRingTime" className="text-left font-medium text-sm">
                      {t('bulkModification.fields.ringTime.title')}
                    </Label>
                  </div>
                  {modifications.applyRingTime && (
                    <div className="ml-6">
                      <Input
                        type="number"
                        value={modifications.ringTime}
                        onChange={(e) => setModifications(prev => ({ ...prev, ringTime: parseInt(e.target.value) || 30 }))}
                        placeholder="30"
                        min="10"
                        max="120"
                        className="text-sm"
                      />
                      <p className="text-xs text-muted-foreground mt-1">
                        {t('bulkModification.fields.ringTime.hint')}
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Call Recording Settings - Full Width */}
              <div className="border border-border rounded-lg p-3">
                <div className="flex items-center gap-2 mb-3">
                  <input
                    type="checkbox"
                    id="applyRecording"
                    checked={modifications.applyRecording}
                    onChange={(e) => setModifications(prev => ({ ...prev, applyRecording: e.target.checked }))}
                    className="rounded border-border"
                  />
                  <Label htmlFor="applyRecording" className="text-left font-medium text-sm">
                    {t('bulkModification.fields.recording.title')}
                  </Label>
                </div>
                {modifications.applyRecording && (
                  <div className="ml-6 space-y-3">
                    {/* Recording toggles */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="flex items-center gap-2">
                        <Toggle
                          id="recordIncoming"
                          value={modifications.recordIncoming}
                          onChange={(value) => setModifications(prev => ({ ...prev, recordIncoming: value }))}
                        />
                        <SimpleTooltip
                          content={buildTooltipContent('bulkModification.fields.recording.recordIncoming', 'extensions.debug.record_incoming')}
                          position="top"
                          maxWidth="300px"
                        >
                          <Label htmlFor="recordIncoming" className="text-left">
                            {t('bulkModification.fields.recording.recordIncoming')}
                          </Label>
                        </SimpleTooltip>
                      </div>
                      <div className="flex items-center gap-2">
                        <Toggle
                          id="recordOutgoing"
                          value={modifications.recordOutgoing}
                          onChange={(value) => setModifications(prev => ({ ...prev, recordOutgoing: value }))}
                        />
                        <SimpleTooltip
                          content={buildTooltipContent('bulkModification.fields.recording.recordOutgoing', 'extensions.debug.record_outgoing')}
                          position="top"
                          maxWidth="300px"
                        >
                          <Label htmlFor="recordOutgoing" className="text-left">
                            {t('bulkModification.fields.recording.recordOutgoing')}
                          </Label>
                        </SimpleTooltip>
                      </div>
                      <div className="flex items-center gap-2">
                        <Toggle
                          id="recordInternal"
                          value={modifications.recordInternal}
                          onChange={(value) => setModifications(prev => ({ ...prev, recordInternal: value }))}
                        />
                        <SimpleTooltip
                          content={buildTooltipContent('bulkModification.fields.recording.recordInternal', 'extensions.debug.record_internal')}
                          position="top"
                          maxWidth="300px"
                        >
                          <Label htmlFor="recordInternal" className="text-left">
                            {t('bulkModification.fields.recording.recordInternal')}
                          </Label>
                        </SimpleTooltip>
                      </div>
                      <div className="flex items-center gap-2">
                        <Toggle
                          id="recordOnDemand"
                          value={modifications.recordOnDemand}
                          onChange={(value) => setModifications(prev => ({ ...prev, recordOnDemand: value }))}
                        />
                        <SimpleTooltip
                          content={buildTooltipContent('bulkModification.fields.recording.recordOnDemand', 'extensions.debug.record_on_demand')}
                          position="top"
                          maxWidth="300px"
                        >
                          <Label htmlFor="recordOnDemand" className="text-left">
                            {t('bulkModification.fields.recording.recordOnDemand')}
                          </Label>
                        </SimpleTooltip>
                      </div>
                    </div>

                    {/* Recording configuration */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 pt-2">
                      <div>
                        <SimpleTooltip
                          content={buildTooltipContent('bulkModification.fields.recording.format', 'extensions.debug.recording_format')}
                          position="top"
                          maxWidth="300px"
                        >
                          <Label htmlFor="recordingFormat" className="text-left text-xs mb-1">
                            {t('bulkModification.fields.recording.format')}
                          </Label>
                        </SimpleTooltip>
                        <select
                          id="recordingFormat"
                          value={modifications.recordingFormat}
                          onChange={(e) => setModifications(prev => ({ ...prev, recordingFormat: e.target.value }))}
                          className="w-full px-3 py-1.5 border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary text-sm"
                        >
                          <option value="wav">WAV</option>
                          <option value="mp3">MP3</option>
                        </select>
                      </div>
                      <div>
                        <SimpleTooltip
                          content={buildTooltipContent('bulkModification.fields.recording.path', 'extensions.debug.record_path')}
                          position="top"
                          maxWidth="300px"
                        >
                          <Label htmlFor="recordPath" className="text-left text-xs mb-1">
                            {t('bulkModification.fields.recording.path')}
                          </Label>
                        </SimpleTooltip>
                        <Input
                          id="recordPath"
                          value={modifications.recordPath}
                          onChange={(e) => setModifications(prev => ({ ...prev, recordPath: e.target.value }))}
                          placeholder="/recordings"
                          className="text-sm py-1.5"
                        />
                      </div>
                      <div>
                        <SimpleTooltip
                          content={buildTooltipContent('bulkModification.fields.recording.template', 'extensions.debug.recording_filename_template')}
                          position="top"
                          maxWidth="300px"
                        >
                          <Label htmlFor="recordTemplate" className="text-left text-xs mb-1">
                            {t('bulkModification.fields.recording.template')}
                          </Label>
                        </SimpleTooltip>
                        <Input
                          id="recordTemplate"
                          value={modifications.recordTemplate}
                          onChange={(e) => setModifications(prev => ({ ...prev, recordTemplate: e.target.value }))}
                          placeholder="${caller_id_number}-${uuid}.wav"
                          className="text-sm py-1.5"
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>

            </div>
          </div>
        </div>
      </div>

      <FixedActionBar
        onSave={handleApply}
        isSaving={isApplying}
        saveDisabled={
          isApplying ||
          (selectionTab === 'individual' && selectedExtensions.length === 0) ||
          (selectionTab === 'ranges' && ranges.length === 0) ||
          (selectionTab === 'patterns' && patterns.length === 0) ||
          (selectionTab === 'filters' && !filters.classOfServicesId && filters.enabled === null && filters.voicemailEnabled === null && filters.billingEnabled === null && !filters.type && !filters.languageCode)
        }
        showCancel={false}
        saveText={
          <>
            <Settings className="h-4 w-4 mr-2" />
            {t('bulkModification.applyChanges')}
          </>
        }
      />

      {/* Extension Selector Modal */}
      <ExtensionSelectorModal
        isOpen={showExtensionSelector}
        onClose={() => { setShowExtensionSelector(false); setExtensionSearch(''); setSearchResults([]); }}
        searchResults={searchResults}
        selectedExtensions={selectedExtensions}
        extensionSearch={extensionSearch}
        isSearching={isSearching}
        isLoadingAll={isLoadingAll}
        onSearchChange={setExtensionSearch}
        onToggleExtension={toggleExtensionInModal}
        onSelectAll={selectAllExtensions}
        t={t}
      />
    </div>
  );
}
